import { useCallback, useEffect, useMemo, useState } from 'react';
import { FlatList, StyleSheet, View, type ListRenderItemInfo } from 'react-native';
import { useRouter } from 'expo-router';
import { SafeAreaView } from 'react-native-safe-area-context';
import { ApiError } from '../../api/client';
import { getWrongQuestions } from '../../api/wrongQuestions';
import { WrongQuestionCard } from '../../components/business/WrongQuestionCard';
import { WrongQuestionFilterBar } from '../../components/business/WrongQuestionFilterBar';
import { AppText } from '../../components/ui/AppText';
import { EmptyView } from '../../components/ui/EmptyView';
import { ErrorView } from '../../components/ui/ErrorView';
import { LoadingView } from '../../components/ui/LoadingView';
import { tokens } from '../../theme/tokens';
import type { WrongQuestionViewModel, WrongQuestionsViewModel } from '../../types/wrongQuestions';

const ALL_FILTER_ID = 'all';

type WrongQuestionsPageState =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'empty' }
  | { status: 'success'; wrongQuestions: WrongQuestionsViewModel };

const isWrongQuestionsEmpty = (wrongQuestions: WrongQuestionsViewModel): boolean =>
  wrongQuestions.questions.length === 0;

const getErrorMessage = (error: unknown): string => {
  if (error instanceof ApiError) {
    if (error.code === 'unauthorized') {
      return '登录状态已失效，请重新登录';
    }
    return error.message;
  }
  return '暂时无法加载错题本';
};

export default function WrongQuestionsTabPage() {
  const router = useRouter();
  const [state, setState] = useState<WrongQuestionsPageState>({ status: 'loading' });
  const [activeFilterId, setActiveFilterId] = useState(ALL_FILTER_ID);

  const loadWrongQuestions = useCallback(async () => {
    setState({ status: 'loading' });
    try {
      const wrongQuestions = await getWrongQuestions();
      if (isWrongQuestionsEmpty(wrongQuestions)) {
        setState({ status: 'empty' });
        return;
      }
      setState({ status: 'success', wrongQuestions });
      setActiveFilterId(ALL_FILTER_ID);
    } catch (error: unknown) {
      setState({ status: 'error', message: getErrorMessage(error) });
    }
  }, []);

  useEffect(() => {
    void loadWrongQuestions();
  }, [loadWrongQuestions]);

  const filteredQuestions = useMemo(() => {
    if (state.status !== 'success' || activeFilterId === ALL_FILTER_ID) {
      return state.status === 'success' ? state.wrongQuestions.questions : [];
    }
    return state.wrongQuestions.questions.filter((question) => question.subject === activeFilterId);
  }, [activeFilterId, state]);

  const openPractice = useCallback(() => {
    router.push('/wrong-question-practice');
  }, [router]);

  if (state.status === 'loading') {
    return (
      <SafeAreaView style={styles.screen}>
        <LoadingView message="正在加载错题本" />
      </SafeAreaView>
    );
  }

  if (state.status === 'error') {
    return (
      <SafeAreaView style={styles.screen}>
        <ErrorView message={state.message} onRetry={loadWrongQuestions} />
      </SafeAreaView>
    );
  }

  if (state.status === 'empty') {
    return (
      <SafeAreaView style={styles.screen}>
        <EmptyView
          title="暂无错题"
          description="练习中的错题会沉淀到这里，方便复盘和重练。"
          actionLabel="重新加载"
          onAction={loadWrongQuestions}
        />
      </SafeAreaView>
    );
  }

  const renderQuestion = ({ item }: ListRenderItemInfo<WrongQuestionViewModel>) => (
    <WrongQuestionCard
      title={item.title}
      subject={item.subject}
      knowledgePoint={item.knowledgePoint}
      retryCount={item.retryCount}
      lastWrongAt={item.lastWrongAt}
      sourceLabel={item.sourceLabel}
      masteryStatus={item.masteryStatus}
      errorType={item.errorType}
      onPractice={openPractice}
    />
  );

  return (
    <SafeAreaView style={styles.screen}>
      <FlatList
        data={filteredQuestions}
        keyExtractor={(item) => item.id}
        renderItem={renderQuestion}
        contentContainerStyle={styles.content}
        ListHeaderComponent={
          <View style={styles.header}>
            <AppText variant="title" weight="bold">
              错题本
            </AppText>
            <AppText variant="body" tone="muted">
              按学科筛选错题，集中复盘未掌握知识点。
            </AppText>
            <WrongQuestionFilterBar
              filters={state.wrongQuestions.filters}
              activeFilterId={activeFilterId}
              onChangeFilter={setActiveFilterId}
            />
          </View>
        }
        ListEmptyComponent={
          <EmptyView
            title="当前筛选暂无错题"
            description="切换学科或完成更多练习后再查看。"
          />
        }
        ItemSeparatorComponent={Separator}
      />
    </SafeAreaView>
  );
}

function Separator() {
  return <View style={styles.separator} />;
}

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    backgroundColor: tokens.colors.background,
  },
  content: {
    paddingHorizontal: tokens.layout.screenPadding,
    paddingTop: tokens.spacing.lg,
    paddingBottom: tokens.spacing.xxl,
  },
  header: {
    gap: tokens.spacing.md,
    marginBottom: tokens.spacing.md,
  },
  separator: {
    height: tokens.spacing.md,
  },
});
